import React, { ReactFragment } from "react"
import { Col, Row, Button, Container, Table } from "react-bootstrap"
import { FaAngleDoubleDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleDoubleUp, FaAngleDown, FaAngleUp, FaChevronLeft, FaChevronRight } from "react-icons/fa"
import _lodash from "lodash"

export interface ColumnDefinition<T> {
  title: string
  value: keyof T | ((data: T) => ReactFragment)
}

interface DualTableBoxProps<T> {
  disabled?: boolean
  idKey: keyof T
  options: T[]
  optionsAtRight: T[]
  leftTitle: string
  rightTitle: string
  leftColumns: ColumnDefinition<T>[]
  rightColumns: ColumnDefinition<T>[]
  hideLeftHeader?: boolean
  hideRightHeader?: boolean
  onChange: (selected: T[]) => void
}

interface DualTableBoxSelection<T> {
  optionsAtRight: T[]
  selectionLeft: T[]
  selectionRight: T[]
}

interface DualTableBoxState<T> extends DualTableBoxSelection<T> {
  optionsAtRight: T[]
}

export default class DualTableBox<T extends object> extends React.PureComponent<DualTableBoxProps<T>, DualTableBoxState<T>> {
  state: DualTableBoxState<T> = {
    optionsAtRight: [...this.props.optionsAtRight],
    selectionLeft: [],
    selectionRight: []
  }

  componentDidUpdate(prevProps: DualTableBoxProps<T>, prevState: DualTableBoxState<T>) {
    if (!_lodash.isEqual(this.props.optionsAtRight, prevProps.optionsAtRight)) {
      this.setState({ optionsAtRight: [...this.props.optionsAtRight] })
    }
    if (!_lodash.isEqual(this.state.optionsAtRight, prevState.optionsAtRight)) {
      const selection = this.props.options.filter(option => this.state.optionsAtRight.includes(option))
      this.props.onChange(selection)
    }
  }

  move(dir: "ltr" | "rtl", all?: boolean) {
    const { selectionLeft, selectionRight, optionsAtRight } = this.state
    const newSelection = all
      ? (dir === "ltr")
        ? this.props.options
        : []
      : (dir === "ltr")
        ? [...optionsAtRight, ...selectionLeft]
        : optionsAtRight.filter(option => !selectionRight.includes(option))

    dir === "ltr"
      ? this.setState({ selectionLeft: [], optionsAtRight: newSelection })
      : this.setState({ selectionRight: [], optionsAtRight: newSelection })
  }

  columnValue = (column: ColumnDefinition<T>, data: T) => {
    if (typeof column.value === "string" && column.value in data) {
      return data[column.value as keyof T]
    } else if (typeof column.value === "function") {
      const columnFn = (column.value as ((data: T) => ReactFragment))
      return columnFn(data)
    }
    return ""
  }

  handleItemClick = (clicked: T, fieldName: keyof DualTableBoxSelection<T>) => {
    if (this.state[fieldName].includes(clicked)) {
      this.setState({ ...{}, [fieldName]: this.state[fieldName].filter(item => item !== clicked) })
    } else {
      this.setState({ ...{}, [fieldName]: [...this.state[fieldName], clicked] })
    }
  }

  render() {
    const table = (options: T[], columns: ColumnDefinition<T>[], selectionFieldName: keyof DualTableBoxSelection<T>, showHeader: boolean) =>
      <Table className="table-sm dark" borderless responsive>
        {showHeader && <thead>
          <tr>
            {columns.map((item, i) => <th key={`${item.title}_${i}`}>{item.title}</th>)}
          </tr>
        </thead>}
        <tbody>
          {options.map((item, i) =>
            <tr key={`${item[this.props.idKey]}_${i}`} className={this.state[selectionFieldName].includes(item) ? "selected" : ""} onClick={() => this.handleItemClick(item, selectionFieldName)}>
              {columns.map((col, j) => <td key={`${item[this.props.idKey]}_${i}_${j}`}>{this.columnValue(col, item)}</td>)}
            </tr>)}
        </tbody>
      </Table>
    const { selectionLeft, selectionRight, optionsAtRight } = this.state
    const options: T[] = this.props.options
    const availableOptions = options.filter(option => !optionsAtRight.includes(option))
    return (
      <>
        <Container fluid>
          <Row className="d-none d-lg-flex">
            <Col lg={5}>{this.props.leftTitle}</Col>
            <Col lg={2} className="px-0"></Col>
            <Col lg={5} className="pl-lg-0">{this.props.rightTitle}</Col>
          </Row>
          <Row>
            <Col className="d-lg-none">{this.props.leftTitle}</Col>
            <Col lg={5} className="pr-lg-0">
              {table(availableOptions, this.props.leftColumns, "selectionLeft", !this.props.hideLeftHeader)}
            </Col>
            <Col lg={2} className="px-0 text-center align-self-center">
              <Container fluid>
                <Row>
                  <Col lg={12} xs={3} className="px-1 text-center">
                    <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!availableOptions.length || this.props.disabled}
                      onClick={() => this.move("ltr", true)}>
                      <FaAngleDoubleRight className="d-none d-lg-inline" />
                      <FaAngleDoubleDown className="d-lg-none" />
                    </Button>
                  </Col>
                  <Col lg={12} xs={3} className="px-1 text-center">
                    <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionLeft.length || this.props.disabled}
                      onClick={() => this.move("ltr")}>
                      <FaChevronRight className="d-none d-lg-inline" />
                      <FaAngleDown className="d-lg-none" />
                    </Button>
                  </Col>
                  <Col lg={12} xs={3} className="px-1 text-center">
                    <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!selectionRight.length || this.props.disabled}
                      onClick={() => this.move("rtl")}>
                      <FaChevronLeft className="d-none d-lg-inline" />
                      <FaAngleUp className="d-lg-none" />
                    </Button>
                  </Col>
                  <Col lg={12} xs={3} className="px-1 text-center">
                    <Button className="btn-sm btn-sm-icon mb-lg-2 mt-lg-0 my-1" disabled={!optionsAtRight.length || this.props.disabled}
                      onClick={() => this.move("rtl", true)}>
                      <FaAngleDoubleLeft className="d-none d-lg-inline" />
                      <FaAngleDoubleUp className="d-lg-none" />
                    </Button>
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col className="d-lg-none">{this.props.rightTitle}</Col>
            <Col lg={5} className="pl-lg-0">
              {table(optionsAtRight, this.props.rightColumns, "selectionRight", !this.props.hideRightHeader)}
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  static defaultProps = { selected: [] }
}
